import { logger } from '@beelday/common'
import { useCallback, useEffect, useState } from 'react'
import { isGoFullscreen } from './model/presenter-messages'
import { enterOwnFullscreen, isOwnFullscreen } from './window-management'

const log = logger.create('presenter-mode')

type ProjectorFullscreen = {
	fullscreen: boolean
	/** Full screen was refused (no delegation in this browser): offer a button. */
	needsClick: boolean
	goFullscreen: () => void
}

/**
 * The presentation window's full screen: from the console's delegated click
 * (`go-fullscreen`), or from a click or key press here.
 */
export const useProjectorFullscreen = (
	presentation: string
): ProjectorFullscreen => {
	const [fullscreen, setFullscreen] = useState(isOwnFullscreen)
	const [needsClick, setNeedsClick] = useState(false)

	const goFullscreen = useCallback(() => {
		enterOwnFullscreen().catch(error => {
			log.warn('Full screen was refused', error)
			setNeedsClick(true)
		})
	}, [])

	useEffect(() => {
		const onFullscreenChange = (): void => {
			setFullscreen(isOwnFullscreen())
			setNeedsClick(false)
		}
		// The console's Show full screen, with the right to do it.
		const onWindowMessage = (event: MessageEvent<unknown>): void => {
			if (
				event.origin === window.location.origin &&
				isGoFullscreen(event.data, presentation)
			) {
				goFullscreen()
			}
		}
		document.addEventListener('fullscreenchange', onFullscreenChange)
		window.addEventListener('message', onWindowMessage)
		return () => {
			document.removeEventListener('fullscreenchange', onFullscreenChange)
			window.removeEventListener('message', onWindowMessage)
		}
	}, [goFullscreen, presentation])

	return { fullscreen, needsClick, goFullscreen }
}
